import { useState, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';

import client from '../../store/connect';
import { fetchSensorData } from '../../store/api';
import { fetchDynLimHistoryData } from '../../store/api';
import { useSelector } from 'react-redux';
import { getTimestamp } from '../../store/ui/uiSelectors';
import { modeIsLive } from '../../store/mode/selectors';
import { createSelector } from 'reselect';
import { trimToWindow, recomputeMinMax } from './sensorDataUtils';
import { DEFAULT_TIMELINE_WINDOW } from '../../constants';

const normalizeData = ({ sensor_value, event_time }) => {
  return {
    value: sensor_value,
    date: new Date(event_time).valueOf(),
  };
};

const normalizeDynamicLimitData = ({ proj_hi, proj_lo, event_time }) => {
  return {
    loLimit: proj_lo,
    hiLimit: proj_hi,
    date: new Date(event_time).valueOf(),
  };
};

const initialSensorState = {
  data: [],
  minValue: Number.POSITIVE_INFINITY,
  maxValue: Number.NEGATIVE_INFINITY,
};

function data(sensorData) {
  return (prevState) => {
    // get highest and lowest value from new data
    // Using Infinity to make sure any number is higher/lower
    const minOfData = sensorData.length
      ? Math.min(...sensorData.map((i) => i.value))
      : Number.POSITIVE_INFINITY;
    const maxOfData = sensorData.length
      ? Math.max(...sensorData.map((i) => i.value))
      : Number.NEGATIVE_INFINITY;
    const { minValue, maxValue } = prevState;

    return {
      data: [...prevState.data, ...sensorData],
      maxValue: Math.max(maxOfData, maxValue),
      minValue: Math.min(minOfData, minValue),
    };
  };
}

const handleDynLimData = (dynLimData) => {
  return (prevState) => {
    // get highest and lowest value from new data
    // Using Infinity to make sure any number is higher/lower
    const minOfData = dynLimData.length
      ? Math.min(...dynLimData.map((i) => i.loLimit))
      : Number.POSITIVE_INFINITY;
    const maxOfData = dynLimData.length
      ? Math.max(...dynLimData.map((i) => i.hiLimit))
      : Number.NEGATIVE_INFINITY;

    const { minValue, maxValue } = prevState;
    return {
      data: [...prevState.data, ...dynLimData],
      maxValue: Math.max(maxOfData, maxValue),
      minValue: Math.min(minOfData, minValue),
    };
  };
};

const getSensorDataTimestamp = createSelector(getTimestamp, (timelineTimestamp) => {
  return timelineTimestamp;
});


export const useSensorData = (sensorName) => {
  const [sensorData, setSensorData] = useState(() => initialSensorState);
  const [dynLimData, setDynLimData] = useState(() => initialSensorState);
  const { TIMELINE_WINDOW: _TIMELINE_WINDOW } = useSelector((state) => state.api.flags);
  if (!_TIMELINE_WINDOW) console.warn('[useSensorData] TIMELINE_WINDOW flag not loaded, using default');
  const TIMELINE_WINDOW = _TIMELINE_WINDOW || DEFAULT_TIMELINE_WINDOW;
  const timestamp = useSelector(getSensorDataTimestamp);
  const isLive = useSelector(modeIsLive);
  const lastSensorFetchTime = useRef(null);
  const lastDynLimFetchTime = useRef(null);

  // Reset refs when switching away from live mode
  useEffect(() => {
    if (!isLive) {
      lastSensorFetchTime.current = null;
      lastDynLimFetchTime.current = null;
    }
  }, [isLive]);

  useEffect(() => {
    const now = DateTime.fromISO(timestamp);
    const windowStart = now.minus(TIMELINE_WINDOW);
    const canDoIncremental = isLive && lastSensorFetchTime.current !== null;
    const from = canDoIncremental ? DateTime.fromISO(lastSensorFetchTime.current) : windowStart;

    fetchSensorData(sensorName, {
      to: now.toISO(),
      from: from.toISO(),
    })
      .then(({ response }) => {
        return { sensorData: response.data.map(normalizeData), mode: response.mode };
      })
      .then(({ sensorData: newData, mode }) => {
        lastSensorFetchTime.current = now.toISO();
        if (canDoIncremental) {
          // Incremental fetch: only accept live data, ignore history fallback
          if (mode === 'live' && newData.length !== 0) {
            setSensorData((prev) => {
              const combined = [...prev.data, ...newData];
              const trimmed = trimToWindow(combined, windowStart.toMillis());
              const { minValue, maxValue } = recomputeMinMax(trimmed, 'value');
              return { data: trimmed, minValue, maxValue };
            });
          }
        } else {
          // Full backfill fetch
          if (newData.length !== 0) {
            if (mode === 'live') {
              setSensorData(() => data(newData)(initialSensorState));
            } else {
              const mockedSensorData = newData.map((data) => {
                return {
                  value: newData[newData.length - 1].value,
                  date: data.date,
                };
              });
              setSensorData(() => data(mockedSensorData)(initialSensorState));
            }
          }
        }
      })
      .catch((err) => {
        console.error('Failed to fetch sensor data:', err);
      });
  }, [timestamp]);

  useEffect(() => {
    const now = DateTime.fromISO(timestamp);
    const windowStart = now.minus(TIMELINE_WINDOW);
    const canDoIncremental = isLive && lastDynLimFetchTime.current !== null;
    const from = canDoIncremental ? DateTime.fromISO(lastDynLimFetchTime.current) : windowStart;

    fetchDynLimHistoryData(sensorName, {
      to: now.toISO(),
      from: from.toISO(),
    })
      .then(({ response }) => {
        return { dynLimData: response.data.map(normalizeDynamicLimitData), mode: response.mode };
      })
      .then(({ dynLimData: newData, mode }) => {
        lastDynLimFetchTime.current = now.toISO();
        if (canDoIncremental) {
          if (mode === 'live' && newData.length !== 0) {
            setDynLimData((prev) => {
              const combined = [...prev.data, ...newData];
              const trimmed = trimToWindow(combined, windowStart.toMillis());
              let minValue = Number.POSITIVE_INFINITY;
              let maxValue = Number.NEGATIVE_INFINITY;
              for (const d of trimmed) {
                if (d.loLimit < minValue) minValue = d.loLimit;
                if (d.hiLimit > maxValue) maxValue = d.hiLimit;
              }
              return { data: trimmed, minValue, maxValue };
            });
          }
        } else {
          if (newData.length !== 0) {
            if (mode === 'live') {
              setDynLimData(() => handleDynLimData(newData)(initialSensorState));
            }
          }
        }
      })
      .catch((err) => {
        console.error('Failed to fetch dynamic limit data:', err);
      });
  }, [timestamp]);

  useEffect(() => {
    // Set up the subscription of the sensor.
    client.emit('subscribe', sensorName);
    return () => {
      // ... and cancel when the component unmounts
      client.emit('unsubscribe', sensorName);
      client.off(sensorName);
    };
  }, []);

  return [sensorData.data, sensorData.minValue, sensorData.maxValue, dynLimData.data];
};
